import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  ArrowRight,
  Check,
  CheckCircle2,
  Copy,
  ExternalLink,
  Laptop,
  Loader2,
  Maximize2,
  MessageSquare,
  Package,
  Power,
  RefreshCw,
  Server,
  Share2,
  Smartphone,
  Tablet,
  Wifi,
  WifiOff
} from 'lucide-react';

const DEVICE_PRESETS = [
  { id: 'iphone', label: 'iPhone 15', width: 393, height: 852, icon: Smartphone, bezel: 'rounded-[50px] border-[10px] border-slate-900 shadow-2xl ring-1 ring-slate-800' },
  { id: 'android', label: 'Android', width: 360, height: 800, icon: Smartphone, bezel: 'rounded-[36px] border-[8px] border-slate-800 shadow-xl ring-1 ring-slate-700' },
  { id: 'tablet', label: 'Планшет', width: 680, height: 880, icon: Tablet, bezel: 'rounded-[28px] border-[12px] border-slate-800 shadow-xl' },
  { id: 'fluid', label: 'На весь экран', width: '100%', height: 820, icon: Maximize2, bezel: 'rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm' },
];

export default function ClientAppSimulator({
  status,
  onRefreshStatus,
  onNavigateSection,
}) {
  const defaultHost = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
  const defaultUrl = `http://${defaultHost}:3001`;

  const [currentDevice, setCurrentDevice] = useState('iphone');
  const [iframeUrl, setIframeUrl] = useState(defaultUrl);
  const [inputUrl, setInputUrl] = useState(defaultUrl);
  const [copied, setCopied] = useState(false);
  const [iframeKey, setIframeKey] = useState(Date.now());
  const [iframeLoading, setIframeLoading] = useState(true);

  const selectedPreset = DEVICE_PRESETS.find((d) => d.id === currentDevice) || DEVICE_PRESETS[0];

  const handleApplyUrl = (newUrl) => {
    const trimmed = (newUrl || inputUrl).trim();
    if (!trimmed) return;
    setInputUrl(trimmed);
    setIframeUrl(trimmed);
    setIframeKey(Date.now());
    setIframeLoading(true);
  };

  const handleReload = () => {
    setIframeKey(Date.now());
    setIframeLoading(true);
    if (onRefreshStatus) onRefreshStatus();
  };

  const handleCopy = () => {
    navigator.clipboard?.writeText(iframeUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Top control bar */}
      <div className="flex flex-col gap-3 rounded-2xl bg-white p-3.5 shadow-sm dark:bg-gray-900 lg:flex-row lg:items-center lg:justify-between">
        {/* Device selector */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="mr-1 text-xs font-semibold uppercase tracking-wider text-gray-400">Устройство:</span>
          {DEVICE_PRESETS.map((preset) => {
            const Icon = preset.icon;
            const active = currentDevice === preset.id;
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => setCurrentDevice(preset.id)}
                className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-medium transition ${
                  active
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {preset.label}
              </button>
            );
          })}
        </div>

        {/* URL switcher & Actions */}
        <div className="flex flex-1 items-center gap-2 lg:max-w-xl">
          <div className="relative flex flex-1 items-center rounded-xl border border-gray-200 bg-gray-50 px-3 py-1.5 focus-within:border-blue-400 focus-within:bg-white dark:border-gray-700 dark:bg-gray-800">
            <span className="mr-2 h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            <input
              type="text"
              value={inputUrl}
              onChange={(e) => setInputUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleApplyUrl()}
              placeholder="http://localhost:3001"
              className="w-full bg-transparent text-xs font-mono text-gray-800 outline-none dark:text-gray-200"
            />
          </div>

          <button
            type="button"
            onClick={() => handleApplyUrl()}
            title="Применить адрес"
            className="rounded-xl border border-gray-200 p-2 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            <ArrowRight className="h-4 w-4" />
          </button>

          <button
            type="button"
            onClick={handleReload}
            title="Перезагрузить экран"
            className="rounded-xl border border-gray-200 p-2 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            <RefreshCw className="h-4 w-4" />
          </button>

          <button
            type="button"
            onClick={handleCopy}
            title="Скопировать ссылку"
            className="rounded-xl border border-gray-200 p-2 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
          </button>

          <a
            href={iframeUrl}
            target="_blank"
            rel="noopener noreferrer"
            title="Открыть в новой вкладке"
            className="rounded-xl bg-blue-50 p-2 text-blue-700 hover:bg-blue-100 dark:bg-blue-950/50 dark:text-blue-300"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </div>

      {/* Main workspace: Simulator + Inspector/Sidebar */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 items-start">
        {/* Simulator phone area */}
        <div className="lg:col-span-8 flex justify-center overflow-x-auto rounded-3xl bg-slate-100/80 p-6 dark:bg-slate-950/60 border border-slate-200/80 dark:border-slate-800">
          <div
            style={{
              width: typeof selectedPreset.width === 'number' ? `${selectedPreset.width}px` : selectedPreset.width,
              height: `${selectedPreset.height}px`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
            className={`relative flex flex-col bg-black overflow-hidden ${selectedPreset.bezel}`}
          >
            {/* Realistic smartphone speaker / Dynamic Island for iPhone */}
            {selectedPreset.id === 'iphone' && (
              <div className="absolute top-2.5 left-1/2 -translate-x-1/2 z-30 h-6 w-28 rounded-full bg-black flex items-center justify-between px-2.5 shadow-md">
                <span className="h-2.5 w-2.5 rounded-full bg-neutral-900 border border-neutral-800" />
                <span className="h-2 w-2 rounded-full bg-blue-950/80" />
              </div>
            )}

            {/* Android Punch Hole */}
            {selectedPreset.id === 'android' && (
              <div className="absolute top-3 left-1/2 -translate-x-1/2 z-30 h-3.5 w-3.5 rounded-full bg-black shadow-inner" />
            )}

            {/* Loading indicator */}
            {iframeLoading && (
              <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/90 p-6 text-center dark:bg-gray-900/90 backdrop-blur-sm">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-2" />
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Подключение к клиенту…</p>
                <p className="text-xs text-gray-500 mt-1 max-w-xs">
                  Если окно остаётся пустым, убедитесь, что клиент запущен на порту 3001
                </p>
              </div>
            )}

            {/* Client App iframe */}
            <iframe
              key={iframeKey}
              src={iframeUrl}
              title="Postirano Client App"
              onLoad={() => setIframeLoading(false)}
              onError={() => setIframeLoading(false)}
              className="h-full w-full border-0 bg-white dark:bg-gray-950"
              allow="geolocation; camera"
            />

            {/* Bottom Home Indicator Bar (iOS style) */}
            {selectedPreset.id === 'iphone' && (
              <div className="pointer-events-none absolute bottom-1.5 left-1/2 -translate-x-1/2 z-30 h-1 w-32 rounded-full bg-black/40 dark:bg-white/40" />
            )}
          </div>
        </div>

        {/* Side panel: Diagnostics & Quick actions */}
        <div className="lg:col-span-4 space-y-3">
          {/* Status widget card */}
          <section className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3 dark:border-gray-800">
              <div className="flex items-center gap-2">
                <Server className="h-4 w-4 text-blue-600" />
                <h3 className="text-sm font-bold text-gray-900 dark:text-white">Статус интеграции</h3>
              </div>
              <button
                type="button"
                onClick={onRefreshStatus}
                className="text-xs text-blue-600 hover:underline"
              >
                Обновить
              </button>
            </div>

            <div className="mt-3 space-y-2.5 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Ключ интеграции BW:</span>
                {status?.integration_configured ? (
                  <span className="inline-flex items-center gap-1 font-medium text-emerald-600">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Настроен
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 font-medium text-amber-600">
                    <AlertCircle className="h-3.5 w-3.5" /> Не задан в .env
                  </span>
                )}
              </div>

              <div className="flex items-center justify-between">
                <span className="text-gray-500">Бэкенд клиента (:5100):</span>
                {status?.client_backend_alive ? (
                  <span className="inline-flex items-center gap-1 font-medium text-emerald-600">
                    <Wifi className="h-3.5 w-3.5" /> Онлайн
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 font-medium text-gray-400">
                    <WifiOff className="h-3.5 w-3.5" /> Не запущен
                  </span>
                )}
              </div>

              <div className="flex items-center justify-between">
                <span className="text-gray-500">Системный актор:</span>
                <span className="font-semibold text-gray-800 dark:text-gray-200">
                  {status?.actor_user?.name || 'ID ' + (status?.actor_user?.id || 1)}
                </span>
              </div>
            </div>

            {/* Quick counters */}
            <div className="mt-4 grid grid-cols-3 gap-2 border-t border-gray-100 pt-3 dark:border-gray-800 text-center">
              <button
                type="button"
                onClick={() => onNavigateSection?.('requests')}
                className="group rounded-xl bg-amber-50 p-2 transition hover:bg-amber-100 dark:bg-amber-950/30 dark:hover:bg-amber-950/50"
              >
                <div className="text-base font-bold text-amber-700 dark:text-amber-300">
                  {status?.pending_requests_count ?? 0}
                </div>
                <div className="text-[10px] text-amber-600 dark:text-amber-400">На проверке</div>
              </button>

              <button
                type="button"
                onClick={() => onNavigateSection?.('chats')}
                className="group rounded-xl bg-blue-50 p-2 transition hover:bg-blue-100 dark:bg-blue-950/30 dark:hover:bg-blue-950/50"
              >
                <div className="text-base font-bold text-blue-700 dark:text-blue-300">
                  {status?.unread_messages_count ?? 0}
                </div>
                <div className="text-[10px] text-blue-600 dark:text-blue-400">Сообщений</div>
              </button>

              <button
                type="button"
                onClick={() => onNavigateSection?.('catalog')}
                className="group rounded-xl bg-emerald-50 p-2 transition hover:bg-emerald-100 dark:bg-emerald-950/30 dark:hover:bg-emerald-950/50"
              >
                <div className="text-base font-bold text-emerald-700 dark:text-emerald-300">
                  {status?.published_catalog_items ?? 0}
                </div>
                <div className="text-[10px] text-emerald-600 dark:text-emerald-400">На витрине</div>
              </button>
            </div>
          </section>

          {/* Quick presets for URLs */}
          <section className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-2">
              Быстрое переключение адреса
            </h4>
            <div className="space-y-1.5">
              <button
                type="button"
                onClick={() => handleApplyUrl(`http://${defaultHost}:3001`)}
                className="w-full flex items-center justify-between rounded-xl border border-gray-100 px-3 py-2 text-left text-xs transition hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800"
              >
                <span className="font-medium text-gray-800 dark:text-gray-200">Локальный клиент (Dev)</span>
                <code className="text-[11px] text-gray-400 font-mono">:3001</code>
              </button>

              <button
                type="button"
                onClick={() => handleApplyUrl('http://192.168.88.6:3001')}
                className="w-full flex items-center justify-between rounded-xl border border-gray-100 px-3 py-2 text-left text-xs transition hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800"
              >
                <span className="font-medium text-gray-800 dark:text-gray-200">Локальная сеть (IP)</span>
                <code className="text-[11px] text-gray-400 font-mono">192.168.88.6:3001</code>
              </button>

              <button
                type="button"
                onClick={() => handleApplyUrl('https://client.bwkin.ru')}
                className="w-full flex items-center justify-between rounded-xl border border-gray-100 px-3 py-2 text-left text-xs transition hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800"
              >
                <span className="font-medium text-gray-800 dark:text-gray-200">Боевой сервер (Prod)</span>
                <code className="text-[11px] text-blue-500 font-mono">client.bwkin.ru</code>
              </button>
            </div>
          </section>

          {/* Test Instructions Cheat Sheet */}
          <section className="rounded-2xl border border-blue-100 bg-blue-50/60 p-4 dark:border-blue-900 dark:bg-blue-950/20 text-xs">
            <h4 className="font-bold text-blue-900 dark:text-blue-200 flex items-center gap-1.5 mb-2">
              <span>💡</span> Шпаргалка для тестирования
            </h4>
            <ul className="space-y-1.5 text-blue-800/90 dark:text-blue-300/90 list-disc pl-4">
              <li>
                <strong>Запуск клиента:</strong> запустите <code className="bg-white/80 dark:bg-gray-900/80 px-1 py-0.5 rounded">PostiranoClient\infrastructure\start_client_app.bat</code>.
              </li>
              <li>
                <strong>Тестовый телефон:</strong> введите любой номер (например, <code className="bg-white/80 dark:bg-gray-900/80 px-1 py-0.5 rounded">+79991234567</code>), код OTP появится в тосте.
              </li>
              <li>
                <strong>Оформление заявки:</strong> отправленный заказ поступит во вкладку <strong>«Заявки»</strong> на проверку.
              </li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}
